import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, RouterStateSnapshot } from '@angular/router';
import { PublicLinkService } from '@simOn/public-link';
import { isDashboardTabAllowed, publicLinkFallbackTab } from '@simOn/public-link/models';
import { Localstorage } from '@simOn/utils/localstorage';

const DASHBOARD_TABS = ['assets', 'tasks', 'events', 'devices', 'scenes', 'resources'] as const;
type DashboardTab = (typeof DASHBOARD_TABS)[number];

const lastVisitedDashboardTab = new Localstorage<DashboardTab>('DashboardLastVisitedTab').load();

/** Remembers the dashboard tab a logged user opened; public link visits leave the user's memory alone. */
export const rememberDashboardTabGuardFn: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  if (inject(PublicLinkService).isPublicLinkModeActive()) return true;
  const tab = state.url.match(/\/dashboard\/([^/?#;]+)/)?.[1];
  if (tab && (DASHBOARD_TABS as readonly string[]).includes(tab) && tab !== lastVisitedDashboardTab.value) {
    lastVisitedDashboardTab.set(tab as DashboardTab);
  }
  return true;
};

/**
 * Tab the bare `dashboard` URL opens: the last visited one, unless the current visitor may not open it
 * (public link without that capability), then the visitor's fallback tab. Runs in an injection context (redirectTo).
 */
export const lastVisitedDashboardTabPath = (): DashboardTab => {
  const tab = lastVisitedDashboardTab.value;
  const remembered: DashboardTab = tab && DASHBOARD_TABS.includes(tab) ? tab : 'assets';
  const access = inject(PublicLinkService).access();
  return isDashboardTabAllowed(remembered, access) ? remembered : publicLinkFallbackTab(access);
};
